import {
  concat,
  find,
  findKey,
  first,
  flow,
  get,
  includes,
  indexOf,
  keys,
  mapValues,
  merge,
  pull,
  set,
  toNumber,
  unset,
  update,
} from 'lodash/fp';
import typeToReducer from 'type-to-reducer';

import { toDoTypes } from './../todos';
import * as types from './types';

const insertAt = (n, ins) => arr => [...arr.slice(0, n), ins, ...arr.slice(n)];

function mergeToDoLists(state, { payload }) {
  return merge(state, payload);
}

const toDoListsInitialState = [];
export default typeToReducer({
  [types.TODOLISTS_MERGE]: mergeToDoLists,
  [types.TODOLIST_CREATE]: {
    FULFILLED: mergeToDoLists,
  },
  [types.TODOLIST_SAVE]: {
    PENDING: (state, action) => {
      const { meta: { toDoListId } } = action;
      return flow(
        update(toDoListId, set('isLoading', true)),
        update(toDoListId, unset('error')),
      )(state);
    },
    REJECTED: (state, action) => {
      const { meta: { toDoListId }, payload } = action;
      const errors = (payload.response && payload.response.data);
      const error = first(get(
        first(keys(errors)),
      )(errors));

      return flow(
        update(toDoListId, unset('isLoading')),
        update(toDoListId, set('error', error || true)),
      )(state);
    },
    FULFILLED: (state, action) => {
      const { meta: { toDoListId } } = action;
      const mergedToDoLists = mergeToDoLists(state, action);
      return flow(
        update(toDoListId, set('isLoading', false)),
        update(toDoListId, unset('error')),
      )(mergedToDoLists);
    },
  },
  [types.TODOLIST_SELECT]: (state, action) => {
    const { meta: { toDoListId } } = action;
    return flow(
      mapValues(set('selected', false)),
      update(toDoListId, set('selected', true)),
    )(state);
  },
  [types.TODOLIST_CHANGE]: (state, action) => {
    const { payload, meta: { toDoListId } } = action;
    return update(toDoListId, toDoList => merge(toDoList, payload))(state);
  },
  [toDoTypes.TODO_CREATE]: {
    FULFILLED: (state, action) => {
      const { payload } = action;
      const toDoId = flow(
        keys,
        first,
        key => toNumber(key) || key,
      )(payload);

      const toDoListId = payload[toDoId].toDoList;

      return update(
        toDoListId, update('toDos', _ => concat(_, toDoId)),
      )(state);
    },
  },
  [toDoTypes.TODO_CHANGE]: (state, action) => {
    const { data, id: toDoId } = action;
    if (data.toDoList) {
      const currentToDoToDoListId = findKey({
        toDos: [toDoId],
      })(state);

      return flow(
        update(currentToDoToDoListId, update('toDos', pull(toDoId))),
        update(data.toDoList, update('toDos', concat(toDoId))),
      )(state);
    }
    return state;
  },
  [toDoTypes.TODO_DELETE]: {
    FULFILLED: (state, action) => {
      const { meta: { id } } = action;
      return mapValues(
        update('toDos', pull(id)),
      )(state);
    },
  },
  [toDoTypes.TODO_SORT]: {
    PENDING: (state, action) => {
      const {
        payload: {
          targetToDoId,
          referenceToDoId,
        },
      } = action;

      const targetList = find(
        elt => includes(targetToDoId)(get('toDos')(elt)),
      )(state);

      const referenceList = find(
        elt => includes(referenceToDoId)(get('toDos')(elt)),
      )(state);

      const todosOnSameList = targetList.id === referenceList.id;

      if (!todosOnSameList) {
        return state;
      }

      const targetPosition = flow(
        get('toDos'),
        indexOf(referenceToDoId),
      )(targetList);

      return update(targetList.id)(flow(
        update('toDos')(pull(targetToDoId)),
        update('toDos')(insertAt(targetPosition, targetToDoId)),
      ))(state);
    },
  },
}, toDoListsInitialState);
